import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const CALLER_COLORS = {
  tom: "#8B5CF6",
  raphael: "#3B82F6",
  theo: "#10B981",
  arthur: "#F59E0B",
  antoine: "#EF4444",
  adam: "#06B6D4",
  dimitri: "#EC4899",
  julien: "#84CC16",
  console: "#6366F1",
  "system-scan": "#9CA3AF",
  "ci-staging": "#D1D5DB",
};

const FALLBACK_COLORS = [
  "#F97316", "#14B8A6", "#A855F7", "#F43F5E", "#0EA5E9", "#D946EF",
];

function getCallerColor(caller, idx) {
  return CALLER_COLORS[caller] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, e) => s + (e.value || 0), 0);
  return (
    <div className='bg-white rounded-xl border border-border-subtle shadow-lg px-4 py-3 max-w-[280px]'>
      <p className='text-body-xs font-semibold text-text-primary mb-1.5'>{label}</p>
      {payload
        .filter((e) => e.value > 0)
        .sort((a, b) => (b.value || 0) - (a.value || 0))
        .map((entry) => (
          <div key={entry.dataKey} className='flex items-center gap-2 text-body-xs'>
            <span className='w-2 h-2 rounded-full flex-shrink-0' style={{ backgroundColor: entry.color }} />
            <span className='text-text-secondary truncate max-w-[140px]'>{entry.name}</span>
            <span className='font-semibold text-text-primary ml-auto'>${entry.value?.toFixed(2)}</span>
          </div>
        ))}
      <div className='border-t border-border-subtle mt-1.5 pt-1.5 flex items-center justify-between text-body-xs'>
        <span className='text-text-secondary font-medium'>Total</span>
        <span className='font-bold text-text-primary'>${total.toFixed(2)}</span>
      </div>
    </div>
  );
};

const TeamUsageTrendChart = ({ data, isLoading, error }) => {
  const { chartData, callerKeys } = useMemo(() => {
    if (!data?.callers?.length) return { chartData: [], callerKeys: [] };

    const allDates = new Set();
    const callerDailyMap = {};

    for (const c of data.callers) {
      if (c.total_cost_usd <= 0) continue;
      callerDailyMap[c.caller] = c.daily || {};
      for (const d of Object.keys(c.daily || {})) {
        allDates.add(d);
      }
    }

    const sortedDates = [...allDates].sort();
    if (!sortedDates.length) return { chartData: [], callerKeys: [] };

    const start = new Date(sortedDates[0] + "T00:00:00");
    const end = new Date(sortedDates[sortedDates.length - 1] + "T00:00:00");
    const filled = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().split("T")[0];
      const entry = { date: iso };
      for (const [caller, daily] of Object.entries(callerDailyMap)) {
        entry[caller] = daily[iso] || 0;
      }
      filled.push(entry);
    }

    const keys = Object.keys(callerDailyMap).sort((a, b) => {
      const sumA = data.callers.find((c) => c.caller === a)?.total_cost_usd || 0;
      const sumB = data.callers.find((c) => c.caller === b)?.total_cost_usd || 0;
      return sumB - sumA;
    });

    return { chartData: filled, callerKeys: keys };
  }, [data]);

  if (isLoading) {
    return (
      <div className='h-[350px] flex items-center justify-center'>
        <div className='flex flex-col items-center gap-2'>
          <div className='animate-spin rounded-full h-6 w-6 border-2 border-accent-purple border-t-transparent' />
          <span className='text-body-sm text-text-tertiary'>Loading usage data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='h-[350px] flex items-center justify-center'>
        <p className='text-body-sm text-severity-high'>Failed to load team usage data</p>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className='h-[350px] flex items-center justify-center'>
        <p className='text-body-sm text-text-tertiary'>No usage data available for this period</p>
      </div>
    );
  }

  return (
    <div className='h-[350px]'>
      <ResponsiveContainer width='100%' height='100%'>
        <BarChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray='3 3' stroke='rgba(10,16,48,0.06)' vertical={false} />
          <XAxis
            dataKey='date'
            tick={{ fontSize: 11, fill: "rgba(10,10,38,0.4)" }}
            axisLine={{ stroke: "rgba(10,16,48,0.08)" }}
            tickLine={false}
            tickFormatter={(v) => {
              const parts = v.split("-");
              return parts.length === 3 ? `${parts[1]}/${parts[2]}` : v;
            }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "rgba(10,10,38,0.4)" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${v}`}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(139,92,246,0.04)" }} />
          <Legend
            iconType='circle'
            iconSize={8}
            wrapperStyle={{ fontSize: "11px", paddingTop: "12px" }}
          />
          {callerKeys.map((key, i) => (
            <Bar
              key={key}
              dataKey={key}
              stackId='stack'
              fill={getCallerColor(key, i)}
              name={key}
              radius={i === callerKeys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TeamUsageTrendChart;
